import {
  createContext,
  useContext,
  useMemo,
  useState,
  type ReactNode,
} from 'react'

/**
 * PoC-only fallback login so the app isn't blocked end-to-end if the Okta
 * flow is unavailable. Never attaches a bearer token, so Okta-protected
 * managerium calls will still fail for users who log in this way.
 */
const VALID_EMAIL = 'admin@n26.com'
const VALID_PASSWORD = '12345!'

type LocalAuthContextValue = {
  isAuthenticated: boolean
  login: (email: string, password: string) => boolean
  logout: () => void
}

const LocalAuthContext = createContext<LocalAuthContextValue | null>(null)

type LocalAuthProviderProps = {
  children: ReactNode
}

export const LocalAuthProvider = ({ children }: LocalAuthProviderProps) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false)

  const value = useMemo<LocalAuthContextValue>(
    () => ({
      isAuthenticated,
      login: (email, password) => {
        const success = email === VALID_EMAIL && password === VALID_PASSWORD
        if (success) setIsAuthenticated(true)
        return success
      },
      logout: () => setIsAuthenticated(false),
    }),
    [isAuthenticated]
  )

  return (
    <LocalAuthContext.Provider value={value}>
      {children}
    </LocalAuthContext.Provider>
  )
}

export const useLocalAuth = (): LocalAuthContextValue => {
  const context = useContext(LocalAuthContext)
  if (!context)
    throw new Error('useLocalAuth must be used within a LocalAuthProvider')
  return context
}
